import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { handleApiCall, RODEPICK_API_CONST } from "src/_config";
import {
  useAppGuestUserIdSelector,
  useAppPickIdsSelector,
  useAppUserDetailsSelector,
} from "src/_common/hooks/selectors/userSelector";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import EmailLoginModal from "./EmailLoginModal";
import UpdateDisplayNameModal from "src/components/events/modal/updateDisplayNameModal";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";

interface OTPVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onVerifySuccess: () => void;
  onResendCode?: () => void;
  consent?: boolean; // Add consent parameter to receive promotional checkbox value
}

const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  onVerifySuccess,
  onResendCode,
  consent = false, // Default to false if not provided
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [phonePrefix] = useState<number>(1); // Default to US/Canada
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showUpdateDisplayNameModal, setShowUpdateDisplayNameModal] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Hooks for handling post-login actions (same as LoginForm.tsx)
  const userApi = useUserApi();
  const toast = useToaster();
  const history = useHistory();
  const guestUserId = useAppGuestUserIdSelector();
  const pickIds = useAppPickIdsSelector();
  const userAction = useAppUserAction();
  const userDetails = useAppUserDetailsSelector();

  // Check for display name after OTP verification
  useEffect(() => {
    if (justLoggedIn && userDetails) {
      setJustLoggedIn(false);

      if (!userDetails.display_name) {
        // User needs to set display name - show the modal
        setShowUpdateDisplayNameModal(true);
      } else {
        // User has display name - just close and call success
        onVerifySuccess();
      }
    }
  }, [justLoggedIn, userDetails]);

  useEffect(() => {
    // console.log("OTPVerificationModal isOpen:", isOpen);
    if (isOpen) {
      // Reset state when modal opens
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setIsLoading(false);
      setResendTimer(30);
      setCanResend(false);

      // Focus first input
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);

      // Start countdown timer
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // const handleInputChange = (index: number, value: string) => {
  //   if (value.length > 1) return; // Only allow single digit
  //   if (!/^\d*$/.test(value)) return; // Only allow numbers

  //   const newOtp = [...otp];
  //   newOtp[index] = value;
  //   setOtp(newOtp);
  //   setError("");

  //   // Auto-focus next input
  //   if (value && index < 5) {
  //     inputRefs.current[index + 1]?.focus();
  //   }

  //   // Removed auto-submit - only submit on button click
  // };

  // const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
  //   if (e.key === "Backspace" && !otp[index] && index > 0) {
  //     // Move to previous input on backspace if current is empty
  //     inputRefs.current[index - 1]?.focus();
  //   }
  // };

  // const handlePaste = (e: React.ClipboardEvent) => {
  //   e.preventDefault();
  //   const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");

  //   if (pastedData.length === 6) {
  //     const newOtp = pastedData.split("").slice(0, 6);
  //     setOtp(newOtp);
  //     setError("");

  //     // Focus last input
  //     inputRefs.current[5]?.focus();

  //     // Removed auto-submit on paste - only submit on button click
  //   }
  // };

  const handleVerifyOTP = async (otpCode?: string) => {
    const codeToVerify = otpCode || otp.join("");

    if (codeToVerify.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");

    // Prepare API parameters (reusing logic from LoginForm.tsx)
    const params = {
      phone: parseInt(phoneNumber),
      otp: parseInt(codeToVerify),
      country_code: phonePrefix,
      is_block: consent ? "0" : "1", // Use consent from SignInModal: 0 = allow promotional messages, 1 = block
      game_slug: RODEPICK_API_CONST,
    };

    // Call OTP verification API (same as LoginForm.tsx)
    handleApiCall(
      userApi.callLoginWithOtp,
      params,
      (_resp: any) => {
        // Handle post-login actions (same as LoginForm.tsx)
        if (guestUserId && pickIds?.length) {
          saveVote();
        }

        // Mark that user just logged in to trigger display name check
        setJustLoggedIn(true);

        // Handle redirect if exists
        const link = localStorage.getItem("redirectLink");
        if (link) {
          localStorage.removeItem("redirectLink");
          history.push(link);
        }

        // Don't call onVerifySuccess immediately - wait for display name check
        // If display name exists or user closes the modal, then success will be called
      },
      toast,
      false,
      true,
      () => {
        setIsLoading(false);
        setError("Invalid verification code. Please try again.");
      },
    );
  };

  // Save guest user picks (reused from LoginForm.tsx)
  const saveVote = () => {
    const params: any = {
      guest_user_id: guestUserId,
      pick_id: pickIds,
    };
    handleApiCall(
      userApi.callSaveUserPicks,
      params,
      (resp: any) => {
        if (resp) {
          userAction.setPickIds(null);
          userAction.setGuestUserId(null);
        }
      },
      toast,
      false,
      true,
    );
  };

  const handleResendCode = () => {
    if (!canResend) return;

    setResendTimer(30);
    setCanResend(false);
    setOtp(["", "", "", "", "", ""]);
    setError("");

    // Restart timer
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // TODO: Implement resend code API call
    onResendCode?.();
    // console.log("Resending code to:", phoneNumber);
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display (e.g., (555) 123-4567)
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEmailLogin = () => {
    // Open email login modal
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    // Close email modal and trigger verify success
    setShowEmailModal(false);
    onVerifySuccess();
  };

  const handleUpdateDisplayNameClose = () => {
    setShowUpdateDisplayNameModal(false);
    // Call success callback after display name modal closes
    onVerifySuccess();
  };

  const handleEmailClose = () => {
    // Close email modal
    setShowEmailModal(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content signin-modal">
            <div className="modal-header border-0">
              <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body text-center">
              {/* Header with Logo */}
              <AuthModalHeader />
              {/* Lock Icon */}
              <div className="signin-icon mb-3">
                <div className="lock-icon">
                  <i className="fas fa-lock"></i>
                </div>
              </div>

              {/* Title */}
              <h2 className="signin-title mb-2">Sign In</h2>
              <p className="signin-subtitle mb-4">Welcome to the arena</p>

              {/* Phone Number Display */}
              <div className="phone-input-container mb-3">
                <div className="input-group">
                  <span className="input-group-text phone-prefix otp-modal-phone-prefix">
                    <i className="fas fa-phone"></i>
                  </span>
                  <input
                    type="tel"
                    className="form-control phone-input otp-modal-phone-input"
                    value={formatPhoneNumber(phoneNumber)}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              {/* OTP Input */}
              <div className="otp-input-container mb-3">
                <div className="input-group">
                  <span className="input-group-text otp-prefix otp-modal-otp-prefix">
                    <i className="fas fa-key"></i>
                  </span>
                  <input
                    type="text"
                    className={`form-control otp-input otp-modal-otp-input ${error ? "is-invalid" : ""}`}
                    placeholder="One Time Code"
                    value={otp.join("")}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      const newOtp = value
                        .split("")
                        .concat(Array(6 - value.length).fill(""))
                        .slice(0, 6);
                      setOtp(newOtp);
                      setError("");
                      // Removed auto-submit functionality - only submit on button click
                    }}
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger otp-error mb-3" role="alert">
                  {error}
                </div>
              )}

              {/* Resend Code Link */}
              <div className="resend-container mb-3">
                {canResend ? (
                  <button className="btn btn-link resend-btn p-0 otp-modal-resend-btn" onClick={handleResendCode}>
                    <i className="fas fa-redo-alt me-1 otp-modal-resend-icon"></i>
                    Resend code
                  </button>
                ) : (
                  <span className="resend-timer otp-modal-resend-timer">
                    <i className="fas fa-clock me-1 otp-modal-resend-icon"></i>
                    Resend in {resendTimer}s
                  </span>
                )}
              </div>

              {/* Log In Button */}
              <button
                className="btn btn-success w-100 mb-3 otp-modal-login-btn"
                onClick={() => handleVerifyOTP()}
                disabled={isLoading || otp.join("").length !== 6}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Verifying...
                  </>
                ) : (
                  "Log In"
                )}
              </button>

              {/* Email Login Link */}
              <div className="email-login-container">
                <button className="btn btn-link email-login-btn p-0" onClick={handleEmailLogin}>
                  Login with email/password
                </button>
              </div>
              {/* Footer Links */}
              <AuthModalFooter />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>

      {/* Email Login Modal - only show when showEmailModal is true */}
      <EmailLoginModal isOpen={showEmailModal} onClose={handleEmailClose} onLoginSuccess={handleEmailSuccess} />

      {/* Update Display Name Modal */}
      <UpdateDisplayNameModal shouldShow={showUpdateDisplayNameModal} onClose={handleUpdateDisplayNameClose} />
    </>
  );
};

export default OTPVerificationModal;
